/*
Site Video
*/

class SiteVideo extends HTMLElement {
  connectedCallback() {
    if (this.dataset.stopOnObserve === "true") {
      this.stopOnObserve();
    } else {
      this.startOnObserve();
    }

    this.playBtn = this.querySelector(".play-button-js");
    this.video = this.querySelector("video");

    if (this.playBtn) {
      this.playBtn.addEventListener("click", () => {
        this.video.play();
      });

      this.video.addEventListener("click", () => {
        if (this.video.paused) {
          this.video.play();
        } else {
          this.video.pause();
        }
      });

      this.video.addEventListener("ended", () => {
        this.playBtn.classList.add("hidden");
      });

      this.video.addEventListener("pause", () => {
        this.playBtn.classList.remove("hidden");
      });

      this.video.addEventListener("play", () => {
        this.playBtn.classList.add("hidden");
      });
    }
  }

  stopOnObserve() {
    if ("IntersectionObserver" in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (video) {
          const $video = video.target;
          if (!video.isIntersecting) {
            $video.pause();
          }
        });
      });

      videoObserver.observe(this.querySelector("video"));
    }
  }

  startOnObserve() {
    if ("IntersectionObserver" in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (video) {
          const $video = video.target;
          if (video.isIntersecting) {
            if ($video.readyState === 4) {
              $video.play();
            } else {
              [].slice
                .call($video.querySelectorAll("source"))
                .forEach((source) => {
                  if (source.dataset.src) {
                    source.src = source.dataset.src;
                  }
                });
              $video.load();
            }
          } else {
            if (!$video.paused) {
              $video.pause();
            }
          }
        });
      });

      videoObserver.observe(this.querySelector("video"));
    }
  }
}

customElements.define("site-video", SiteVideo);

/*
Site Image
*/

class SiteImage extends HTMLElement {
  connectedCallback() {
    this.observe();
  }

  disconnectedCallback() {
    this.unobserve();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      this.imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((el) => {
          if (this.dataset.fill === "false") {
            const parent = this.closest(".image-cont");
            const img = new Image();
            img.src = this.dataset.src;
            img.onload = function () {
              parent.style.aspectRatio = `${this.width} / ${this.height}`;
              parent.style.position = `relative`;
              parent.classList.add("active");
            };
          }
          if (el.isIntersecting) {
            const target = this.querySelector(".image-full");
            const srcset =
              target.dataset.srcset || target.getAttribute("srcset");
            const img = new Image();
            img.srcset = srcset;
            img.onload = () => {
              target.srcset = srcset;
              this.classList.add("active");
              this.imageObserver.unobserve(this);
            };
          }
        });
      });

      const img = this.querySelector(".image-light");
      if (img) {
        this.imageObserver.observe(img);
      }
    }
  }

  unobserve() {
    this.imageObserver.disconnect();
  }
}

customElements.define("site-image", SiteImage);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 6, spaceBetween: 20 };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
    this.handleSlideChange();
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    // we can have multiple pagers in one carousel
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          nextPager.classList.add("active");
        });
      });
    }
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
 * Bullets Carousel
 */

class BulletsCarousel extends SiteCarousel {
  connectedCallback() {
    const config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 6, spaceBetween: 20 };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
    this.handleSlideChange();

    this.buttonsCarousel = this.querySelectorAll(".button-carousel");

    this.relatedSwiper = document.querySelector(".bullets-buttons-img-js");
    this.slideToImage();
  }

  slideToImage() {
    this.buttonsCarousel.forEach((button, index) => {
      button.addEventListener("click", () => {
        this.relatedSwiper.slideTo(index);
      });
    });
  }
}

customElements.define("bullets-carousel", BulletsCarousel);

/*
Flyout Toggle
*/

class FlyoutToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Flyout toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const flyout = this.closest("site-flyout");
      if (flyout) {
        if (flyout.classList.contains("active")) {
          flyout.close();
        } else {
          flyout.open();
        }
      }
    });
  }
}

customElements.define("flyout-toggle", FlyoutToggle);

/*
Site Flyout
*/

class SiteFlyout extends HTMLElement {
  constructor() {
    super();
    this.flyout = null;
  }

  connectedCallback() {
    this.flyout = document.querySelector("site-flyout");
    this.loader = this.flyout.querySelector(".flyout-loader");
    this.target = this.flyout.querySelector(".flyout-target");
    this.content = this.flyout.querySelector(".flyout-content");
  }

  showLoader() {
    this.loader.classList.remove("hidden");
  }

  hideLoader() {
    this.loader.classList.add("hidden");
  }

  open() {
    document.body.style.overflow = "hidden";
    this.flyout.classList.add("active");
  }

  close() {
    document.body.removeAttribute("style");
    this.flyout.classList.remove("active");
    this.empty();
  }

  empty() {
    this.content.classList.remove("active");
    this.target.innerHTML = "";
  }

  render(html) {
    this.target.innerHTML = html;
    this.content.classList.add("active");
  }
}

customElements.define("site-flyout", SiteFlyout);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Filters Toggle
*/

class FiltersToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Filters toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const filters = document.querySelector("product-filters");
      if (filters) {
        filters.toggleFilters();
      }
    });
  }
}

customElements.define("filters-toggle", FiltersToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
    this.openCart = true;
  }

  connectedCallback() {
    this.openCart = this.dataset.openCart === "false" ? false : true;
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          await cart.addVariantToCart(
            $id.value,
            qty,
            {
              selling_plan: sellingPlan,
            },
            this.openCart
          );
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Cart Quantity Update
*/

class CartQuantityUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "change",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = e.target.dataset.index;
        const val = e.target.value;

        if (cart) {
          await cart.updateQuantity(idx, val);
        }
      }, 300)
    );
  }
}

customElements.define("cart-quantity-update", CartQuantityUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.body = document.querySelector("body");
    this.header = this.querySelector("header");
    this.hamburgerButton = document.querySelector(".hamburger-button-js");
    this.closeButton = document.querySelector(".close-hamburger-button-js");
    this.hamburgerMenuContainer = document.querySelector(
      ".hamburger-menu-content-js"
    );
    this.hamburgerButton.addEventListener("click", () =>
      this.openHamburgerMenu()
    );
    this.closeButton.addEventListener("click", () => this.closeHamburgerMenu());

    this.submenuButtonToOpen = this.querySelector(".has-submenu");
    this.submenuContainer = this.querySelector(".submenu-container-js");

    this.allLinks = document.querySelectorAll("a");
    this.allLinks.forEach((link) => {
      link.addEventListener("click", () => this.closeHamburgerMenu());
    });

    this.subMenuToggle();
    this.scrollChangeHeaderStyle();
  }

  scrollChangeHeaderStyle() {
    document.addEventListener("DOMContentLoaded", () => {
      const checkScroll = () => {
        if (window.scrollY > 0) {
          this.header.classList.add("active");
        } else {
          this.header.classList.remove("active");
        }
      };

      window.addEventListener("scroll", checkScroll);
      checkScroll();
    });
  }

  subMenuToggle() {
    this.submenuButtonToOpen.addEventListener("mouseenter", () =>
      this.openSublinksMenu()
    );
    this.submenuButtonToOpen.addEventListener("mouseleave", () =>
      this.closeSublinksMenu()
    );

    this.submenuContainer.addEventListener("mouseenter", () =>
      this.openSublinksMenu()
    );
    this.submenuContainer.addEventListener("mouseleave", () =>
      this.closeSublinksMenu()
    );
  }

  openSublinksMenu() {
    this.submenuContainer.classList.add("active");

    this.header.classList.add("active");
    this.submenuButtonToOpen.style.color = "#EFFE53";
  }
  closeSublinksMenu() {
    this.submenuContainer.classList.remove("active");
    if (window.scrollY == 0) {
      this.header.classList.remove("active");
    }
    this.submenuButtonToOpen.style.color = "";
  }

  openHamburgerMenu() {
    this.hamburgerMenuContainer.classList.remove("invisible", "opacity-0");
    this.hamburgerMenuContainer.classList.add("opacity-95");
    this.body.classList.add("overflow-hidden");
    this.hamburgerMenuContainer.classList.remove("-translate-y-[100%]");
  }

  closeHamburgerMenu() {
    this.hamburgerMenuContainer.classList.add("invisible", "opacity-0");
    this.hamburgerMenuContainer.classList.remove("opacity-95");
    this.hamburgerMenuContainer.classList.add("-translate-y-[100%]");

    this.body.classList.remove("overflow-hidden");
  }

  cartOrFlyoutOpen(ctx) {
    const menuToggle = this.header.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      if (ctx === "cart") {
        this.header
          .querySelector('[data-trigger="close-cart"')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
      if (ctx === "flyout") {
        menuToggle.querySelector("span:nth-child(1)").classList.add("hidden");
        menuToggle
          .querySelector("span:nth-child(2)")
          .classList.remove("hidden");
      }
      if (ctx === "filters") {
        this.header
          .querySelector('[data-trigger="close-filters"]')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
    }
  }
}

customElements.define("site-header", SiteHeader);

/*
  Hamburger Menu Links
*/

class HamburgerMenuLinks extends HTMLElement {
  connectedCallback() {
    this.shopButtonExpand = this.querySelector(".shop-button-expand-js");
    this.defaultHamburgerLinks = this.querySelector(
      ".default-hamburger-links-js"
    );
    this.shopAllLinks = this.querySelector(".shop-all-links-js");

    this.prevToDefaultLinksButton = this.querySelector(
      ".prev-to-default-links-js"
    );

    this.shopButtonExpand.addEventListener("click", () => {
      this.defaultHamburgerLinks.classList.add("hidden");
      this.shopAllLinks.classList.remove("hidden");
    });

    this.prevToDefaultLinksButton.addEventListener("click", () => {
      this.defaultHamburgerLinks.classList.remove("hidden");
      this.shopAllLinks.classList.add("hidden");
    });
  }

  removeActiveClassFromOtherElements(clickedContainer) {
    const containers = this.querySelectorAll(".link__item-container");
    containers.forEach((container) => {
      if (
        container !== clickedContainer &&
        container.classList.contains("active")
      ) {
        container.classList.remove("active");
        container.querySelector(".link__list").style.maxHeight = "0";
        container.querySelector(".link__item-plus").classList.remove("hidden");
        container.querySelector(".link__item-minus").classList.add("hidden");
      }
    });
  }
}

customElements.define("hamburger-menu-links", HamburgerMenuLinks);

/*
  Collapse Menu Links
*/

class CollapseMenuLinks extends HTMLElement {
  constructor() {
    super();
    this.renderLinks();
  }

  removeActiveClassFromOtherElements(clickedContainer) {
    const containers = document.querySelectorAll(".link__item-container");
    containers.forEach((container) => {
      if (
        container !== clickedContainer &&
        container.classList.contains("active")
      ) {
        container.classList.remove("active");
        container.querySelector(".link__list").style.maxHeight = "0";
        container.querySelector(".link__item-plus").classList.remove("hidden");
        container.querySelector(".link__item-minus").classList.add("hidden");
      }
    });
  }

  renderLinks() {
    const linkItem = this.querySelector(".link__item");
    const container = this.querySelector(".link__item-container");
    const linkList = this.querySelector(".link__list");
    const plus = this.querySelector(".link__item-plus");
    const minus = this.querySelector(".link__item-minus");

    linkItem.addEventListener("click", () => {
      this.removeActiveClassFromOtherElements(container);
      container.classList.toggle("active");

      if (container.classList.contains("active")) {
        linkList.style.maxHeight = linkList.scrollHeight + "px";
        plus.classList.add("hidden");
        minus.classList.remove("hidden");
      } else {
        linkList.style.maxHeight = "0";
        plus.classList.remove("hidden");
        minus.classList.add("hidden");
      }
    });
  }
}

customElements.define("collapse-menu-links", CollapseMenuLinks);

/*
  Search Toggle
*/

class SearchToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      const button = e.target;
      const search = document.querySelector("search-bar");
      const searchButton = document.querySelector(
        ".site-header search-toggle button"
      );

      searchButton.classList.toggle("underline");
      search.classList.toggle("active");
      search.querySelector('input[type="search"]').focus();
    });
  }
}

customElements.define("search-toggle", SearchToggle);

/*
  Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
    }
  }

  toggleCart() {
    const header = document.querySelector("site-header");
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
      header.cartOrFlyoutOpen("cart");
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "main-cart-recommendations",
        selector: "#main-cart-recommendations",
      },
      {
        section: "header",
        selector: "#header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        const html = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
        elToReplace.innerHTML = html;
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, options, openCart = true) {
    const items = [options ? { id, quantity, ...options } : { id, quantity }];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active") && openCart) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);

        return response;
      })
      .catch((e) => {
        // handle error
      });
  }

  updateQuantity(line, quantity) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
      })
      .catch(() => {
        // handle error
      });
  }

  async getCart() {
    try {
      const response = await fetch("/cart.js");
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error get cart: ", error);
      return null;
    }
  }

  wipeCart() {
    return fetch("/cart/clear.js").then((response) => response.json());
  }

  updateCart(updates) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
        this.handleRewards();
      })
      .catch(() => {
        // handle error
      });
  }
}

customElements.define("site-cart", SiteCart);

/*
Cart Footer
 */

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector("select");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.select.value;
    const button = event.target.closest("button");

    if (button) {
      let newValue = parseInt(this.select.value);

      if (button.name === "increment") {
        newValue = Math.min(newValue + 1, 9);
      } else if (button.name === "decrement") {
        newValue = Math.max(newValue - 1, 1);
      }

      this.select.value = newValue;

      if (previousValue !== this.select.value) {
        this.select.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.content = null;
  }

  connectedCallback() {
    this.icons = this.querySelectorAll("svg");
    this.details = this.querySelector("details");
    this.content = this.querySelector(".content");
    this.details.addEventListener("click", (e) => {
      e.preventDefault();
      this.animateHeight();
    });
  }

  animateHeight() {
    if (this.details.open) {
      this.icons.forEach((icon) => {
        icon.classList.toggle("hidden");
      });
      this.content.style.maxHeight = "0px";
      setTimeout(() => {
        this.details.open = false;
      }, 300);
    } else {
      this.details.open = true;
      this.icons.forEach((icon) => {
        icon.classList.toggle("hidden");
      });
      this.content.style.maxHeight = this.content.scrollHeight + "px";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

class NewsletterForm extends HTMLElement {
  connectedCallback() {
    const listId = this.dataset.listId;
    const closeModal = document.querySelector(".subscribe-close");
    const success = document.querySelector(".subscribe-modal");

    this.addEventListener("submit", (e) => {
      e.preventDefault();
      const $form = this.querySelector("form");
      const $email = this.querySelector('[type="email"]');
      const $submit = this.querySelector('[type="submit"]');
      const $error = this.querySelector(".error");
      const submitText = $submit.innerHTML;
      const email = $email.value;
      const body = { email, listId };

      $submit.innerHTML = this.dataset.loadingText;
      $error.classList.add("hidden");

      const config = {
        body: JSON.stringify(body),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
      };

      if (listId) {
        try {
          fetch(
            "https://hunni-serverless.vercel.app/api/ourx/subscribe",
            config
          )
            .then((res) => res.json())
            .then((data) => {
              $form.reset();
              $email.blur();
              $submit.innerHTML = submitText;
              success.classList.toggle("active");
              window.cvg({
                method: "track",
                eventName: "Subscribed To Newsletter",
                properties: {
                  form: "Footer",
                  formId: "XLSVK5",
                },
                profileProperties: {
                  $email: email,
                },
                aliases: [`urn:email:${email}`],
              });
              setTimeout(() => {
                success.classList.toggle("active");
              }, 4000);
            });
        } catch (e) {
          $submit.innerHTML = submitText;
          $error.classList.remove("hidden");
        }
      }
    });

    closeModal.addEventListener("click", () => {
      success.classList.toggle("active");
    });
  }
}

customElements.define("newsletter-form", NewsletterForm);

class CheckboxWithAccessibility extends HTMLElement {
  constructor() {
    super();
  }

  isSelected() {
    const item = this.querySelector("#chkPref");
    return item.getAttribute("aria-checked") === "true";
  }

  connectedCallback() {
    const textCheckbox = this.querySelector("#chk1-label");
    const checkbox = this.querySelector("#chkPref");

    textCheckbox.addEventListener("keydown", function (event) {
      if (event.keyCode === 32) {
        changeCheckbox();
      }
    });

    const changeCheckbox = () => {
      const item = this.querySelector("#chkPref");
      const checked = item.getAttribute("aria-checked");

      if (checked === "true") {
        item.setAttribute("aria-checked", "false");
        item.setAttribute("checked", "false");
      } else {
        item.setAttribute("aria-checked", "true");
        item.setAttribute("checked", "true");
      }
    };

    textCheckbox.addEventListener("click", changeCheckbox);
    checkbox.addEventListener("click", changeCheckbox);
  }
}
customElements.define("checkbox-with-accessibility", CheckboxWithAccessibility);

class ProductItem extends HTMLElement {
  connectedCallback() {
    this.selectInputs = Array.from(this.querySelectorAll("input[type=radio]"));
    this.cartAddIdEl = this.querySelector("cart-add input[name='id']");
  }
}

customElements.define("product-item", ProductItem);

/*
Cart Item
*/
class CartItem extends HTMLElement {
  connectedCallback() {
    this.optionSelectorsSelectEls = Array.from(
      this.querySelectorAll(".option-selectors select")
    );
    this.quantityInputEl = this.querySelector("number-input select");
    this.removeButtonEl = this.querySelector(".remove-button-js");
    this.product = JSON.parse(this.dataset.product);
    this.selectedVariant = JSON.parse(this.dataset.selectedVariant);
    this.optionsWithValues = JSON.parse(this.dataset.productOptionsWithValues);
    this.cartIndex = this.dataset.cartIndex;

    this.hideUnavailableOptions();
    this.addOptionChangeEvents();
    this.addQuantityChangeEvent();
    this.addRemoveEvent();
  }

  hideUnavailableOptions() {
    this.optionSelectorsSelectEls.forEach((select) => {
      const options = Array.from(select.querySelectorAll("option"));
      options.forEach((option) => {
        const predictedOption = CartItem.predictVariantOption(
          this.product.variants,
          this.optionsWithValues,
          this.selectedVariant,
          option.dataset.optionName,
          option.value
        );

        if (!predictedOption.available) {
          option.setAttribute("disabled", "");
        }
      });
    });
  }

  addOptionChangeEvents() {
    this.optionSelectorsSelectEls.forEach((select) => {
      select.addEventListener("change", () => {
        const siteCart = document.querySelector("site-cart");
        const selectedOptions = this.optionSelectorsSelectEls.map(
          (select) => select.value
        );
        const newVariant = CartItem.findVariantByOptions(
          this.product.variants,
          selectedOptions
        );
        const quantityVal = +this.quantityInputEl.value;
        siteCart.updateCart({
          [this.selectedVariant.id]: 0,
          [newVariant.id]: quantityVal,
        });
      });
    });
  }

  addQuantityChangeEvent() {
    this.quantityInputEl.addEventListener("change", () => {
      const siteCart = document.querySelector("site-cart");
      const quantityVal = +this.quantityInputEl.value;
      siteCart.updateCart({ [this.selectedVariant.id]: quantityVal });
    });
  }

  addRemoveEvent() {
    this.removeButtonEl.addEventListener("click", () => {
      const siteCart = document.querySelector("site-cart");
      siteCart.updateCart({ [this.selectedVariant.id]: 0 });
    });
  }

  static predictVariantOption(
    variants,
    optionsWithValues,
    selectedVariant,
    optionName,
    value
  ) {
    const predictedOptions = optionsWithValues.map((option) => {
      if (option.name === optionName) {
        return value;
      }

      return selectedVariant.options.find((opt) => option.values.includes(opt));
    });

    const predictedVariant = CartItem.findVariantByOptions(
      variants,
      predictedOptions
    );

    return {
      available: predictedVariant.available,
      priceDiff: predictedVariant.price - selectedVariant.price,
      variant: predictedVariant,
    };
  }

  static findVariantByOptions(variants, options) {
    const variant = variants.find((variant) => {
      const currentOptsStr = JSON.stringify(options.sort());
      const variantOptsStr = JSON.stringify(variant.options.sort());
      return currentOptsStr === variantOptsStr;
    });

    return variant;
  }
}

customElements.define("cart-item", CartItem);

/*
Select Input
*/

class SelectInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const target = this.querySelector(".select-target");
    const targetText = target.innerHTML;

    this.querySelector("select").addEventListener("change", (e) => {
      const options = Array.from(this.querySelectorAll("option"));
      const active = options.find((o) => o.value === e.target.value);

      if (!!e.target.value) {
        target.innerHTML = active.innerHTML;
        target.classList.add("text-gray");
      } else {
        target.innerHTML = targetText;
        target.classList.remove("text-gray");
      }
    });
  }
}

customElements.define("select-input", SelectInput);

class SiteMarquee extends HTMLElement {
  constructor() {
    super();
    const duration = this.dataset.duration;
    const shadowRoot = this.attachShadow({ mode: "open" });
    shadowRoot.innerHTML = this.getInnerHTML();

    const width = this.querySelector('[slot="content"]').scrollWidth / 5;
    shadowRoot.innerHTML = this.getInnerHTML(width, duration);
  }

  getInnerHTML(width = 0, duration = "20s") {
    return `
      <style>
        ::slotted([slot="content"]) {
          display: grid;
          grid-auto-flow: column;
          align-items: center;
          height: 100%;
        }
        .marquee-list {
          display: grid;
          grid-auto-flow: column;
          align-items: center;
          white-space: nowrap;
          animation: marquee ${duration} linear infinite;
          height: 100%;
        }

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${width}px);
          }
        }
      </style>
      <div class="marquee-cont">
        <div class="marquee-list">
          <slot name="content"></slot>
        </div>
      </div>
    `;
  }
}

customElements.define("site-marquee", SiteMarquee);

class CollectionFiltering extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;

      const filter = btn.innerText.replace(/ /g, "-").toLowerCase();
      btn.classList.toggle("active");

      const sections = document.querySelectorAll("[data-grid]");
      sections.forEach((section) => {
        this.filterSection(section);
      });
    });
  }

  filterSection(section) {
    const activeFilters = Array.from(
      this.querySelectorAll("button.active")
    ).map((btn) => btn.innerText.replace(/ /g, "-").toLowerCase());

    const products = section.querySelectorAll("product-item");

    if (activeFilters.length === 0) {
      products.forEach((product, index) => {
        product.classList.toggle("hidden", index >= 5);
      });
    } else {
      products.forEach((product) => {
        const rawTags = product.dataset.tags || "";
        const tags = rawTags.replace(/ /g, "-").toLowerCase().split(",");
        const matchesFilter = activeFilters.some((filter) =>
          tags.includes(filter)
        );
        product.classList.toggle("hidden", !matchesFilter);
      });
    }
  }
}

customElements.define("collection-filters", CollectionFiltering);

class DiscountCode extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
    this.loadSavedCode();
  }

  setupListeners() {
    const input = this.querySelector('input[name="code"]');
    const button = this.querySelector("button");
    this.sectionId = this.dataset.sectionId;
    button.addEventListener("click", async (event) => {
      event.preventDefault();
      button.disabled = true;
      if (input.value !== "") {
        button.innerText = "...";
        try {
          await fetch(`/discount/${input.value}`);

          const response = await fetch(`/cart?section_id=${this.sectionId}`);
          const data = await response.text();
          const siteCart = document.querySelector("site-cart");
          const discountInput = new DOMParser()
            .parseFromString(data, "text/html")
            .querySelector("discount-code");

          // Save the discount code to local storage
          localStorage.setItem("discountCode", input.value);
          this.showDiscountCode(input.value);

          if (discountInput) {
            this.replaceWith(discountInput);
            siteCart.updateCart();
            console.log("Discount applied");
          } else {
            console.error("Failed to retrieve new cart content.");
          }

          if (!discountInput.hasAttribute("manual-discount")) {
            console.warn("None discount applied.");
          }
        } catch (error) {
          console.error("Error applying discount code:", error);
        }
      } else {
        console.error("Discount code input not found.");
      }

      button.disabled = false;
    });
  }

  loadSavedCode() {
    const savedCode = localStorage.getItem("discountCode");
    if (savedCode) {
      this.showDiscountCode(savedCode);
    }
  }

  showDiscountCode(code) {
    const discountCodeNameDiv = this.querySelector(".discount-code-name-js");
    const closeIcon = `<svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;
    discountCodeNameDiv.innerHTML = `
      ${code} <button class="remove-discount font-apercu-mono">${closeIcon}</button>
    `;
    discountCodeNameDiv
      .querySelector(".remove-discount")
      .addEventListener("click", async (event) => {
        localStorage.removeItem("discountCode");
        discountCodeNameDiv.innerHTML = "";

        event.preventDefault();
        try {
          await fetch(`/discount/clear_discount`);

          const siteCart = document.querySelector("site-cart");
          siteCart.updateCart();
          console.log("Discount Removed");
        } catch (error) {
          console.error("Error removing discount code:", error);
        }
      });
  }
}

customElements.define("discount-code", DiscountCode);

class SubItemMenu extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    this.itemTitle = this.querySelector(".sub-item-title-js");
    this.itemsContainer = this.querySelector(".sub-items-js");

    this.itemTitle.addEventListener("mouseenter", () => {
      this.resetAllMenus();
      this.itemTitle.classList.add("underline");
      this.itemsContainer.classList.remove("hidden");
      this.itemsContainer.classList.add("flex");
    });
  }

  resetAllMenus() {
    const allTitles = document.querySelectorAll(".sub-item-title-js");

    allTitles.forEach((item) => {
      item.classList.remove("underline");
    });
    const allMenus = document.querySelectorAll(".sub-items-js");

    allMenus.forEach((item) => {
      item.classList.add("hidden");
    });
  }
}

customElements.define("sub-item-menu", SubItemMenu);

/*
Site Modal
*/

class SiteModal extends HTMLElement {
  connectedCallback() {
    this.registerEvents();
    this.modal = this.querySelector(".modal");
  }

  openModal() {
    if (this.modal) {
      this.modal.classList.add("active");
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.remove("active");
    }
  }

  registerEvents() {
    const opens = this.querySelectorAll(".modal-open");
    const closes = this.querySelectorAll(".modal-close");
    const triggerClass = this.dataset.trigger;
    const trigger = document.querySelector(triggerClass);

    if (trigger) {
      trigger.addEventListener("click", (e) => {
        this.openModal();
      });
    }

    Array.from(opens).forEach((open) => {
      open.addEventListener("click", (e) => {
        console.log("open");
        this.openModal();
      });
    });

    Array.from(closes).forEach((close) => {
      close.addEventListener("click", (e) => {
        this.closeModal();
      });
    });
  }
}

customElements.define("site-modal", SiteModal);

/* Character Limt */

class CharacterLimit extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const textContent = this.textContent.trim();
    const maxLength = this.getAttribute("data-max-length");

    this.textContent =
      textContent.length > maxLength
        ? textContent.substring(0, maxLength) + "..."
        : textContent;
  }
}

customElements.define("character-limit", CharacterLimit);

/* Swivel Form */

class SwivelForm extends HTMLElement {
  connectedCallback() {
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const styles = this.querySelector('[name="service"]').value;
      const hairTypes = this.querySelector('[name="hairType"]').value;
      const city = this.querySelector("[name='state']").value;
      const attrs = { styles, hairTypes, city };

      window.open(SwivelForm.getSwivelRedirectUrlFromAttrs(attrs), "_blank");
    });
  }

  static getSwivelRedirectUrlFromAttrs(attrs) {
    const queryParams = SwivelForm.objectToQueryParams(attrs);
    return `https://swivelbeauty.com/search${queryParams && "?"}${queryParams}`;
  }

  static objectToQueryParams(obj) {
    return Object.entries(obj)
      .filter(([key, value]) => {
        // Filter out empty values
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return false;
        }
        return true;
      })
      .map(([key, value]) => {
        // Handle arrays by serializing each value with the same key
        if (Array.isArray(value)) {
          return value
            .map(
              (item) => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
            )
            .join("&");
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join("&");
  }
}

customElements.define("swivel-form", SwivelForm);

class ProductLearnMore extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.getAttribute('data-product-handle');
    if (this.getAttribute('data-auto-fetch') === "true") {
      this.sectionResquets();
    }
  }

  async sectionResquets() {
    const learnMoreModalContent = this.querySelector(".learn-more-modal-content-js")
    const url = `/products/${this.productHandle}/?section=main-product`;

    fetch(url)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const simpleModalEl = doc.querySelector(".simple-modal-js");
        const linkArrow = simpleModalEl.querySelector(".link-arrow-js")
        learnMoreModalContent.innerHTML = "";
        if (linkArrow) linkArrow.innerHTML = "";
        learnMoreModalContent.append(simpleModalEl);

      })
      .catch(error => console.error('Error:', error));
  }
}

customElements.define("product-learn-more", ProductLearnMore);

/* Simple Modal */

class SimpleModal extends HTMLElement {

  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    this.modalContainer = this.querySelector('.modal-container-js')
    this.buttonOpenModal = this.querySelector('.button-js')
    this.buttonCloseModal = this.querySelector('.close-modal-js')
    this.modalContent = this.querySelector('.modal-content-js')

    if (this.modalContent) {
      this.buttonCloseModal && this.buttonCloseModal.addEventListener('click', () => {
        this.closeModal()
      })
      this.buttonOpenModal && this.buttonOpenModal.addEventListener('click', () => {
        console.log("test button modal")
        this.openModal()
      })
      this.modalContainer.addEventListener('click', (event) => {
        if (!this.modalContent.contains(event.target)) {
          this.closeModal();
        }
      });
    }
  }

  openModal() {
    this.modalContainer.classList.add('active')
    document.querySelector('body').style.overflow = "hidden"
  }

  closeModal() {
    this.modalContainer.classList.remove('active')
    document.querySelector('body').style.overflow = "auto"

  }
}

customElements.define("simple-modal", SimpleModal);

/*
  Highlight Description
*/

class HighlightDescription extends HTMLElement {
  connectedCallback() {
    this.applyHighlighting();
  }

  applyHighlighting() {
    const group0 = ['hydration', 'detoxing', 'growth', 'repair', 'hair health'];
    const group1 = ['volume', 'natural', 'nourished', 'length'];
    const group2 = ['breakage', 'itching', 'frizz', 'dry', 'damage'];
    const group3 = ['buildup', 'shine', 'balanced', 'shedding'];
    const colors = ['#EFFE53', '#323E4E', '#EB5228', '#DACFC1'];

    const elements = this.querySelectorAll('span, strong, em');

    elements.forEach(element => {
      const textContent = element.textContent?.trim().replace(/[.,]/g, '');

      if (textContent) {

        if (group0.includes(textContent)) {
          element.style.backgroundColor = colors[0];
          this.elementStyle(element);
        } else if (group1.includes(textContent)) {
          element.style.color = 'white';
          element.style.backgroundColor = colors[1];
          this.elementStyle(element);
        } else if (group2.includes(textContent)) {
          element.style.color = 'white';
          element.style.backgroundColor = colors[2];
          this.elementStyle(element);
        } else if (group3.includes(textContent)) {
          element.style.backgroundColor = colors[3];
          this.elementStyle(element);
        }
      }
    });
  }

  elementStyle(element) {
    element.style.padding = '0px 4px 0px 4px';
    element.style.fontWeight = '700';
    element.style.fontSize = '14px';
    element.style.display = "inline-flex";
    element.style.justifyContent = "center";
    element.style.alignItems = "center";
    element.style.verticalAlign = "middle";
  }
}

customElements.define('highlight-description', HighlightDescription);

class GetQueryParam extends HTMLElement {
  connectedCallback() {
    const paramName = this.getAttribute("query-param");
    if (!paramName) return;

    const params = new URLSearchParams(window.location.search);
    const paramValue = params.get(paramName) || null;

    paramValue && paramValue != "undefined" && (this.textContent = paramValue);
  }
}

customElements.define("get-query-param", GetQueryParam);

class SelectableButtons extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.selectedRadioValue = 3;
  }

  connectedCallback() {
    this.setupButtonListeners();
    this.cartItemsList();
    this.observeCartChanges();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupButtonListeners() {
    const radioButtons = this.querySelectorAll('input[type="radio"][name="selectable-button"]');
    const buttons = this.querySelectorAll('button');
    const checkoutButton = this.querySelector('#checkout-button');

    radioButtons.forEach((radio) => {
      radio.addEventListener('change', (event) => this.updateSelection(event.target, buttons));
      if (radio.checked) {
        this.updateSelection(radio, buttons);
      }
    });

    checkoutButton.addEventListener("click", () => this.checkoutLogic())
  }

  updateSelection(selectedRadio, buttons) {
    buttons.forEach((button) => button.classList.remove('border-2', 'border-[#343D4C]'));

    const selectedButton = selectedRadio.closest('button');
    if (selectedButton) {
      selectedButton.classList.add('border-2', 'border-[#343D4C]');
    }

    this.selectedRadioValue = selectedRadio.id.replace('selectable-button-', '');

    if (this.dataCart) {
      if (this.dataCart?.items.length > 0) {
        this.checkoutStyle(true)
      } else {
        this.checkoutStyle(false)
      }
    }
  }

  async checkoutLogic() {
    const siteCart = document.querySelector("site-cart");
    const cartItemsList = document.querySelector(".cart-items");
    const listItems = cartItemsList ? cartItemsList.querySelectorAll("li") : [];

    if (this.selectedRadioValue == 1) {
      if (listItems.length > 1) {
        window.location.href = "/checkout";
      }
    } else {
      await siteCart.wipeCart();
      const bundleProductVariantId = this.getAttribute('data-bundle-product-variant-id');
      const bundleProductSellingPlanId = this.getAttribute('data-bundle-product-selling-plan-id');

      const response = await siteCart.addVariantToCart(
        bundleProductVariantId,
        1,
        this.selectedRadioValue == 3 ? { selling_plan: bundleProductSellingPlanId } : null,
        false
      );

      if (response && response.success) {
        window.location.href = "/checkout";
      }
    }
  }

  checkoutStyle(status = true) {
    const checkoutButton = this.querySelector('#checkout-button');
    const checkoutArrow = checkoutButton.querySelector('#checkout-arrow');

    if (status == false && this.selectedRadioValue == 1) {
      checkoutArrow.classList.remove("flex");
      checkoutButton.classList.remove("lg:hover:bg-[#8B4513]");
      checkoutArrow.classList.add("hidden");
      checkoutButton.style.opacity = "0.6";
    } else {
      checkoutArrow.classList.remove("hidden");
      checkoutButton.classList.add("lg:hover:bg-[#8B4513]");
      checkoutArrow.classList.add("flex");
      checkoutButton.style.opacity = "1";
    }
  }

  async fetchCartData() {
    const siteCart = document.querySelector("site-cart");

    if (siteCart) {
      try {
        const data = await siteCart.getCart();
        this.dataCart = data;
        return data;
      } catch (error) {
        console.error("Error:", error);
      }
    } else {
      console.error("SiteCart component not found");
    }
  }

  async cartItemsList() {
    const data = await this.fetchCartData();
    if (!data) return;

    const cartTotalPrice = document.querySelector(".cart-total-price");
    const cartItemsList = document.querySelector(".cart-items");
    const templateItem = cartItemsList.querySelector("li[data-template]");

    cartTotalPrice.textContent = `$${(data.total_price / 100).toFixed(2)}`;
    cartItemsList.querySelectorAll("li:not([data-template])").forEach(item => item.remove());

    data.items.forEach((item) => {
      const newItem = templateItem.cloneNode(true);
      newItem.removeAttribute("data-template");
      newItem.querySelector(".cart-item-title").textContent = item.title;
      newItem.querySelector(".cart-item-price").textContent = `$${(item.price / 100).toFixed(2)}`;
      cartItemsList.appendChild(newItem);
    });

    if (data?.items.length > 0) {
      this.checkoutStyle(true)
    } else {
      this.checkoutStyle(false)
    }
  }


  observeCartChanges() {
    const siteCart = document.querySelector("site-cart");

    if (siteCart) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" || mutation.type === "attributes") {
            this.cartItemsList();
          }
        });
      });

      this.observer.observe(siteCart, { childList: true, subtree: true, attributes: true });
    } else {
      console.error("SiteCart component not found");
    }
  }

}

customElements.define('selectable-buttons', SelectableButtons);
